import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Clock } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { getPost } from "@/lib/posts";
import { BRAND } from "@/lib/content";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        headline: post.title,
        description: post.metaDescription,
        datePublished: post.date,
        dateModified: post.date,
        url: `https://dbpropertyconsultant.co.uk/blog/${post.slug}`,
        author: {
          "@type": "Person",
          name: "Declan Bishop",
          jobTitle: "Property Consultant",
          url: "https://dbpropertyconsultant.co.uk/about",
        },
        publisher: {
          "@type": "Organization",
          name: "Declan Bishop — Property Consultant",
          url: "https://dbpropertyconsultant.co.uk",
        },
        mainEntityOfPage: `https://dbpropertyconsultant.co.uk/blog/${post.slug}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://dbpropertyconsultant.co.uk/" },
          { "@type": "ListItem", position: 2, name: "Blog", item: "https://dbpropertyconsultant.co.uk/blog" },
          { "@type": "ListItem", position: 3, name: post.title, item: `https://dbpropertyconsultant.co.uk/blog/${post.slug}` },
        ],
      },
    ],
  };

  return (
    <SiteLayout pageTitle={post.title} pageDescription={post.metaDescription} pagePath={`/blog/${post.slug}`}>
      <JsonLd id={`post-schema-${post.slug}`} data={schema} />

      <section data-testid="post-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-14 pb-16 md:pt-20 md:pb-20">
          <nav data-testid="post-breadcrumb" className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-brand-navy/55">
            <Link to="/" className="hover:text-brand-navy">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-brand-navy">Blog</Link>
          </nav>
          <div className="mt-10 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] tracking-[0.22em] uppercase font-semibold">
            <span className="text-brand-champagne">{post.category}</span>
            <span className="text-brand-navy/45">{post.dateLabel}</span>
            <span className="inline-flex items-center gap-1.5 text-brand-navy/45">
              <Clock size={12} />
              {post.readTime}
            </span>
          </div>
          <h1
            data-testid="post-headline"
            className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-brand-navy leading-[1.05] tracking-tight max-w-4xl"
          >
            {post.title}
          </h1>
          <div className="mt-8 text-[11px] tracking-[0.24em] uppercase text-brand-navy/55 font-semibold">
            By Declan Bishop · AssocRICS · MCIOB · BDMA.Tech
          </div>
        </div>
      </section>

      <article data-testid="post-body" className="bg-white">
        <div className="container-narrow py-16 md:py-24">
          <div className="max-w-3xl space-y-12">
            {post.sections.map((s, i) => (
              <section key={i}>
                {s.heading && (
                  <h2 className="font-serif text-2xl md:text-3xl text-brand-navy tracking-tight mb-5">
                    {s.heading}
                  </h2>
                )}
                <div className="space-y-5 text-base md:text-lg text-brand-navy/80 leading-relaxed">
                  {s.paragraphs.map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </article>

      <section data-testid="post-cta" className="bg-brand-navy text-brand-cream">
        <div className="container-narrow py-20 md:py-24 grid grid-cols-12 gap-y-10 md:gap-x-12 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
              {post.cta.overline}
            </div>
            <h2 className="mt-6 font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight">
              {post.cta.heading} <span className="italic font-light text-brand-champagne">{post.cta.italic}</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="post-cta-primary"
              className="inline-flex items-center justify-between gap-3 bg-brand-champagne text-brand-navy px-7 py-5 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-champagne-soft transition-colors"
            >
              Request a Quote
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="post-cta-call"
              className="inline-flex items-center justify-between gap-3 border border-brand-cream/30 text-brand-cream px-7 py-5 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-cream hover:text-brand-navy transition-colors"
            >
              Call {BRAND.phone}
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
